import { colors, gridSize } from '@atlaskit/theme';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { getConfig } from '../../config';
import { getViewPortDimensions, redirect } from '../../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';

import ErrorBoundary from './../ErrorBoundary/ErrorBoundary';
import leftImageSrc from './default-left.svg';
import rightImageSrc from './default-right.svg';
import InterimFooter from './InterimFooter';
import InterimHeader from './InterimHeader';
import InterimJoinCards from './InterimJoinCards';

const { atlassianAccountUrl } = getConfig();

const contentWidthDesktop = `${gridSize() * 50}px`;
const scalingImagesMaxWidth = `${gridSize() * 150}px`; // 50 grid size for content + 2 * 50 grid size for images
const skipImagesMaxWidth = `${gridSize() * 88}px`;
const widescreenBackgroundImageSize = `calc((100% - ${contentWidthDesktop})/2 - ${gridSize() * 4}px) auto`;
const Background = styled.div`
  display: flex;
  background-image: url(${leftImageSrc}), url(${rightImageSrc});
  background-repeat: no-repeat, no-repeat;
  background-attachment: fixed, fixed;
  background-color: ${colors.N10};
  background-size: ${gridSize() * 46}px auto, ${gridSize() * 46}px auto;
  background-position: left bottom, right bottom;

  @media (max-width: ${scalingImagesMaxWidth}) {
    background-size: ${widescreenBackgroundImageSize}, ${widescreenBackgroundImageSize}, cover;
  }

  @media (max-width: ${skipImagesMaxWidth}) {
    background: none;
  }
  min-height: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 auto;
  padding: 0 ${gridSize()}px;
  width: ${contentWidthDesktop};
  min-height: 100%;
`;

export const YourAccountLinkContainer = styled.p`
  margin: ${gridSize() * 2}px 0 ${gridSize() * 4}px;
  text-align: center;
`;

type Props = {
  atlOrigin: string;
  utmSource: string;
} & AnalyticsConsumerProps;

class InterimHomePage extends React.Component<Props> {
  componentDidMount() {
    const { atlOrigin, utmSource } = this.props;
    this.props.analytics.page('joinableSitesScreen', {
      atlOrigin,
      utmSource,
      ...getViewPortDimensions(),
    });
  }

  handleAccountLinkClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const url = event.currentTarget.href; // save before await because React will reuse this Event instance for bubbling
    await this.props.analytics.ui(
      'link',
      'clicked',
      {},
      { actionSubjectId: 'accountLink', source: 'joinableSitesScreen' },
    );
    redirect(url);
  };

  render() {
    return (
      <ErrorBoundary subject="interim">
        <Background>
          <Content data-test-id="interim-home-page-content">
            {/* The <div> is needed for the sticky footer */}
            <div data-test-id="interim-home-page-main">
              <InterimHeader />
              <InterimJoinCards />
              <YourAccountLinkContainer>
                <a href={`${atlassianAccountUrl}/manage-profile`} onClick={this.handleAccountLinkClick}>
                  <FormattedMessage {...messages.accountLink} />
                </a>
              </YourAccountLinkContainer>
            </div>
            <InterimFooter />
          </Content>
        </Background>
      </ErrorBoundary>
    );
  }
}

export default withAnalytics(InterimHomePage);

const messages = defineMessages({
  accountLink: {
    defaultMessage: 'Go to your account',
    description: "A link to the current user's account",
    id: 'uchi.interimHome.joinCard.accountLink',
  },
});
